import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import { makeAccessor } from '../../../core/utils/makeAccessor.js';

/**
 * Returns `[root, ...descendants]` in BFS order (root first, then direct
 * children, then grandchildren, …) using a pre-built `childrenByParentId`
 * map. Callers should build the map once via `buildFlameIndex` and reuse it
 * across renders — this walk is `O(subtree)`, never `O(data)`.
 *
 * A `visited` set guards against cyclic data (a corrupt dataset where a
 * parent id points at one of its own descendants) so the walk terminates.
 * The queue is drained with a head cursor instead of `Array.prototype.shift()`
 * — `.shift()` reindexes the queue and would degrade the walk to `O(n²)`.
 * @internal
 */
export function collectSubtree<TData extends ChartDatapoint>(
  root: TData,
  rootId: unknown,
  childrenByParentId: ReadonlyMap<unknown, TData[]>,
  idAccessor: string,
): TData[] {
  const readId = makeAccessor(idAccessor);

  const result: TData[] = [root];
  const queue: unknown[] = [rootId];
  const visited = new Set<unknown>([rootId]);
  let head = 0;

  while (head < queue.length) {
    const currentId = queue[head++];
    const children = childrenByParentId.get(currentId);
    if (!children) {
      continue;
    }
    for (const child of children) {
      const childId = readId(child);
      if (visited.has(childId)) {
        continue;
      }
      visited.add(childId);
      result.push(child);
      queue.push(childId);
    }
  }

  return result;
}
